import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import RecaptchaCheckbox from '../RecaptchaCheckbox';
import { useSubscribeNewsletter, useUnsubscribeNewsletter } from '../../hooks/useLeadCapture';

interface NewsletterFormColors {
  primaryColor: string;
  secondaryColor: string;
  buttonColor: string;
  accentColor: string;
}

interface NewsletterFormPreviewProps {
  colors: NewsletterFormColors;
  /** When true, the form actually subscribes the email (used by the public embed page). Defaults to a static preview. */
  interactive?: boolean;
}

// Live-styled preview of the public Newsletter Subscription Form, modeled after Job Board Settings' preview card.
export default function NewsletterFormPreview({ colors, interactive = false }: NewsletterFormPreviewProps) {
  const subscribe = useSubscribeNewsletter();
  const unsubscribe = useUnsubscribeNewsletter();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [mode, setMode] = useState<'subscribe' | 'unsubscribe'>('subscribe');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState('');
  const [requestError, setRequestError] = useState('');
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!interactive) return;
    if (!captchaToken) {
      setCaptchaError('Please complete the CAPTCHA verification.');
      return;
    }

    setCaptchaError('');
    setRequestError('');
    const onSettled = () => {
      setCaptchaToken(null);
      recaptchaRef.current?.reset();
    };
    const onError = (error: unknown) => {
      const apiError = error as { code?: string; message?: string };
      setRequestError(apiError.code === 'CAPTCHA_FAILED'
        ? 'Please complete the CAPTCHA verification.'
        : apiError.message || 'Unable to update your subscription. Please try again.');
    };

    if (mode === 'subscribe') {
      subscribe.mutate({ email, firstName: '', captchaToken }, {
        onSuccess: () => setSubmitted(true), onError, onSettled,
      });
    } else {
      unsubscribe.mutate({ email, captchaToken }, {
        onSuccess: () => setSubmitted(true), onError, onSettled,
      });
    }
  };

  const isPending = subscribe.isPending || unsubscribe.isPending;

  return (
    <div
      style={{ backgroundColor: colors.secondaryColor }}
      className="rounded-lg p-5"
    >
      <div
        style={{ backgroundColor: colors.primaryColor }}
        className="rounded-lg p-5 text-white"
      >
        <p className="text-xs uppercase tracking-wide opacity-70">Stay Updated</p>
        <h3 className="mt-1 text-xl font-bold">Newsletter Subscription</h3>
      </div>

      {interactive && (
        <div className="mt-3 grid grid-cols-2 rounded-lg bg-white p-1" role="group" aria-label="Newsletter action">
          <button type="button" onClick={() => { setMode('subscribe'); setCaptchaToken(null); setCaptchaError(''); setRequestError(''); recaptchaRef.current?.reset(); }} className={`rounded-md px-3 py-2 text-sm font-semibold ${mode === 'subscribe' ? 'bg-slate-100 text-slate-900' : 'text-slate-500'}`}>Subscribe</button>
          <button type="button" onClick={() => { setMode('unsubscribe'); setCaptchaToken(null); setCaptchaError(''); setRequestError(''); recaptchaRef.current?.reset(); }} className={`rounded-md px-3 py-2 text-sm font-semibold ${mode === 'unsubscribe' ? 'bg-slate-100 text-slate-900' : 'text-slate-500'}`}>Unsubscribe</button>
        </div>
      )}

      <form onSubmit={submit} className="mt-3 flex flex-col gap-3 rounded-lg bg-white p-4">
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2"
          style={{ borderColor: colors.accentColor }}
        />

        {interactive && <RecaptchaCheckbox ref={recaptchaRef} onChange={(token) => { setCaptchaToken(token); setCaptchaError(''); }} disabled={isPending} />}
        {captchaError && <p className="text-sm text-red-600">{captchaError}</p>}
        {requestError && <p className="text-sm text-red-600">{requestError}</p>}

        <button
          type="submit"
          disabled={interactive && isPending}
          style={{ backgroundColor: colors.buttonColor }}
          className="rounded-lg px-4 py-2 text-sm font-semibold text-white"
        >
          {interactive && isPending ? 'Submitting...' : mode === 'subscribe' ? 'Subscribe' : 'Unsubscribe'}
        </button>
      </form>

      {submitted && (
        <p className="mt-2 text-xs font-medium" style={{ color: colors.accentColor }}>
          {mode === 'subscribe' ? "You're subscribed!" : "You've been unsubscribed."}
        </p>
      )}
    </div>
  );
}
